import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { hot } from "react-hot-loader";
import Fuse from "fuse.js";
import produce from "immer";
import {
  throttle,
  forEach,
  keys,
  map,
  get,
  reduce,
  isArray,
  isUndefined,
  cloneDeep
} from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableSortLabel,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Tooltip,
  IconButton,
  Grid,
  Switch,
  Input,
  InputAdornment
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import withRouter from "react-router-dom/withRouter";
import { renderValues } from "../../utils/functions";
import Stack from "../ui/Stack";
import _actions from "../../_actions";
import _reducers from "../../_reducers";
import { Search } from "@material-ui/icons";

let fuse;
const allowEditing = false;

const useStyles = makeStyles({
  root: {
    width: "100%"
  },
  tableWrapper: {
    maxHeight: "calc(100vh - 302px)",
    overflow: "auto"
  },
  cell: {
    cursor: "pointer",
    "& #input": {
      display: props => (props.editing ? "block" : "none")
    },
    "& #value": {
      display: props => (!props.editing ? "block" : "none")
    }
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  }
});

const desc = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const stableSort = (array, cmp) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
};

const getSorting = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
};

const useToolbarStyles = makeStyles(theme => ({
  root: {
    minHeight: "auto",
    background: "#fafafa",
    color: "inherit",
    padding: theme.spacing(1, 1, 1, 1.5)
  },
  title: {
    flex: "1 1 100%"
  }
}));

const EnhancedTableToolbar = props => {
  const classes = useToolbarStyles();
  const { title, setSearch } = props;

  return (
    <Toolbar
      className={classes.root}
      style={{ paddingBottom: "2rem", borderBottom: "1px solid #ddd" }}
    >
      <Typography className={classes.title} variant="h6" id="tableTitle">
        {title}
      </Typography>

      <Input
        type="search"
        placeholder="Search"
        endAdornment={
          <InputAdornment position="end">
            <Search />
          </InputAdornment>
        }
        onChange={throttle(e => setSearch(e.target.value), 500)}
      />
      <Box ml={2}>
        <Tooltip title="Columns">
          <IconButton
            onClick={props.onSettingsClick}
            aria-label="filter columns"
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Toolbar>
  );
};

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick = () => {},
    order,
    orderBy,
    numSelected = 0,
    rowCount = 0,
    onRequestSort,
    columns
  } = props;
  const createSortHandler = property => event => {
    onRequestSort(event, property);
  };

  return (
    <>
      {/* <TableHead>
      <TableRow> */}
      {/* <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
          />
        </TableCell> */}
      {map(columns, (column, key) => (
        <TableCell
          key={key}
          sortDirection={orderBy === key ? order : false}
          align={column.align}
          style={{
            minWidth: column.minWidth || column.label.length * 14
          }}
        >
          <TableSortLabel
            active={orderBy === key}
            direction={order}
            onClick={createSortHandler(key)}
          >
            {column.label}
            {orderBy === key ? (
              <span className={classes.visuallyHidden}>
                {order === "desc" ? "sorted descending" : "sorted ascending"}
              </span>
            ) : null}
          </TableSortLabel>
        </TableCell>
      ))}
      {/* </TableRow>
    </TableHead> */}
    </>
  );
}

const DataTable = ({
  schema: schemaP,
  columnPreferences,
  setColumnPreferences,
  match,
  data,
  onRowClick = () => {},
  actions
}) => {
  const [schema, setSchema] = useState(schemaP);
  useEffect(() => {
    setSchema(schemaP);
  }, [schemaP]);

  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("");

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [focused, setFocused] = React.useState({});
  const [search, setSearch] = React.useState();
  const [showColumnsFilter, setShowColumnsFilter] = React.useState(false);
  const [columns, setColumns] = React.useState(
    columnPreferences || { ...schema.properties }
  );
  const classes = useStyles();
  const mapObjectKeyToId = items => {
    let d;
    if (!isArray(items)) {
      // const mapped = reduce(
      //   items,
      //   (arr, item, key) => {
      //     arr.push({ ...item, id: key });
      //     return arr;
      //   },
      //   []
      // );

      const mapped = reduce(
        cloneDeep(items),
        (arr, item, key) => {
          forEach(schema.properties, (val, key) => {
            item[key] = renderValues({
              value: item[key],
              relation: val.relation,
              properties: val.properties,
              maxLength: 50
            });
          });

          arr.push({ ...item, id: key });
          return arr;
        },
        []
      );

      d = mapped;
    } else {
      d = items;
    }

    fuse = new Fuse(d, {
      keys: keys(schema.properties),
      shouldSort: true,
      threshold: 0.1,
      location: 0,
      distance: 3,
      maxPatternLength: 32,
      minMatchCharLength: 1
    });
    return d;
  };

  const [rows, setRows] = useState(mapObjectKeyToId(data) || []);

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const toggleColumn = columnKey => e => {
    const newColumns = produce(columns, draft => {
      draft[columnKey].show = e.target.checked;
      return draft;
    });

    setColumnPreferences({ path: schema.ref, columns: newColumns });
  };

  const getColumns = () =>
    reduce(
      columns,
      (acc, column, key) => {
        if (isUndefined(column.show) || column.show) {
          acc[key] = column;
        }
        return acc;
      },
      {}
    );

  useEffect(() => {
    if (data) {
      if (search) {
        setRows(fuse.search(search));
      } else {
        setRows(mapObjectKeyToId(data));
      }
    }

    if (columnPreferences) {
      setColumns(columnPreferences);
    }
  }, [data, columnPreferences, search]);

  let tabIndex = 0;
  return (
    <>
      <Paper className={classes.root} elevation={5}>
        <EnhancedTableToolbar
          setSearch={setSearch}
          title={`${schema.title} (${rows.length})`}
          onSettingsClick={() => setShowColumnsFilter(true)}
        />
        <div className={classes.tableWrapper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {actions && (
                  <TableCell
                    key="actions"
                    align="right"
                    style={{ width: 10 }}
                  ></TableCell>
                )}
                {/* {map(getColumns(), (column, key) => (
                  <TableCell
                    key={key}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth || column.label.length * 14
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))} */}
                <EnhancedTableHead
                  classes={classes}
                  // numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  columns={getColumns()}
                  // onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rows.length}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(rows, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, rowIndex) => {
                  return (
                    <TableRow
                      onClick={() => onRowClick(row)}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.id}
                    >
                      {actions && (
                        <TableCell align="left">
                          {map(actions, action => (
                            <IconButton
                              key={action.label}
                              onClick={e => {
                                e.stopPropagation();
                                action.onClick(row);
                              }}
                            >
                              {action.icon}
                            </IconButton>
                          ))}
                        </TableCell>
                      )}
                      {map(getColumns(), (column, key) => {
                        const value = row[key];

                        const editing =
                          allowEditing &&
                          focused.rowIndex === rowIndex &&
                          focused.key === key;
                        return (
                          <TableCell
                            onDoubleClick={() => setFocused({ rowIndex, key })}
                            className={classes.cell}
                            tabIndex={tabIndex++}
                            key={`${rowIndex}-${key}`}
                            align={column.align}
                          >
                            {editing ? (
                              <TextField
                                margin="none"
                                autoFocus
                                value={value}
                              />
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          style={{ backgroundColor: "#eee" }}
          rowsPerPageOptions={[10, 25, 50, 100, 250, 500, 1000]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "previous page"
          }}
          nextIconButtonProps={{
            "aria-label": "next page"
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
      <Stack
        title="Select Columns"
        size="small"
        onClose={() => setShowColumnsFilter(false)}
        show={showColumnsFilter}
      >
        {map(columns, (column, key) => (
          <Box key={key} border={1} borderColor="grey.200" p={1} pl={2} pr={2}>
            <Grid container alignItems="center">
              <Grid item xs={10}>
                {column.label}
              </Grid>
              <Grid item xs={2}>
                <Box textAlign="right">
                  <Switch
                    checked={isUndefined(column.show) || column.show}
                    color="primary"
                    onChange={toggleColumn(key)}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
    </>
  );
};

export default connect(
  ({ appState }, props) => ({
    columnPreferences: get(appState.columnPreferences, props.schema.ref)
  }),
  { setColumnPreferences: _actions.setColumnPreferences }
)(withRouter(hot(module)(DataTable)));
